/**
 * AdminInviteModal — invite a new admin user.
 *
 * Form: email + display_name + role multi-select. Submit POSTs to
 * /v1/admin/admins, which creates the Cognito user (welcome email +
 * temp password) and inserts the admin_users + admin_user_roles rows
 * in a DB transaction.
 *
 * Validation is server-authoritative — we surface BadRequestError /
 * ConflictError messages from the API in the form.
 */
import { useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { X } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';
import { toast } from '@/hooks/useToast';

interface Role {
  id: string;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  member_count: number;
  permission_count: number;
}

interface Props {
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

export function AdminInviteModal({ onClose, onSuccess }: Props) {
  const apiUrl = useApiUrl();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleKeys, setRoleKeys] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminFetch(`${apiUrl}/v1/admin/roles`);
        const data: Role[] = await res.json();
        if (!cancelled) setRoles(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load roles');
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const toggleRole = (key: string) => {
    setRoleKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  };

  const canSubmit =
    email.trim().length > 0 &&
    displayName.trim().length > 0 &&
    roleKeys.length > 0 &&
    !submitting;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await adminFetch(`${apiUrl}/v1/admin/admins`, {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          display_name: displayName.trim(),
          role_keys: roleKeys,
        }),
      });
      toast({
        title: 'Admin invited',
        description: `${email.trim()} will receive a welcome email with a temporary password.`,
      });
      await onSuccess();
    } catch (e: any) {
      setError(e?.message || 'Failed to invite admin');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-card p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">Invite User</h3>
            <p className="text-sm text-muted-foreground">
              Cognito will email a temporary password.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1 hover:bg-muted disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="invite-email" className="mb-1 block text-sm font-medium">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={submitting}
              className="block w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-50"
              placeholder="admin@uniquedatingcompany.com"
            />
          </div>
          <div>
            <label htmlFor="invite-name" className="mb-1 block text-sm font-medium">
              Display name
            </label>
            <input
              id="invite-name"
              type="text"
              required
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              disabled={submitting}
              className="block w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-50"
              placeholder="Jane Doe"
            />
          </div>
          <fieldset className="rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">Roles</legend>
            {rolesLoading ? (
              <p className="text-sm text-muted-foreground">Loading roles…</p>
            ) : roles.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No roles defined. (Migration 210 should have seeded the catalog.)
              </p>
            ) : (
              <div className="space-y-2">
                {roles.map(r => (
                  <label
                    key={r.id}
                    className="flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={roleKeys.includes(r.key)}
                      onChange={() => toggleRole(r.key)}
                      disabled={submitting}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {r.name}{' '}
                        <code className="text-xs text-muted-foreground">
                          ({r.key})
                        </code>
                      </div>
                      {r.description && (
                        <div className="text-xs text-muted-foreground">
                          {r.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border bg-card px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? 'Sending invite…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
